import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Plus, Search, Star, Eye, EyeOff, Archive, Pencil } from "lucide-react";

export default function AdminTalents() {
  const [talents, setTalents] = useState([]);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/admin/talents").then((r) => setTalents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = async (t, fields) => {
    try { await api.put(`/admin/talents/${t.id}`, fields); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  const archive = async (t) => {
    if (!window.confirm(`Archive ${t.name}? They will be hidden from the public site but not deleted.`)) return;
    await api.delete(`/admin/talents/${t.id}`); load(); toast.success("Archived");
  };

  const createNew = async () => {
    try {
      const { data } = await api.post("/admin/talents", { name: "New talent", published: false });
      navigate(`/admin/talents/${data.id}`);
    } catch (e) { toast.error("Create failed"); }
  };

  const dateAdded = (t) => {
    const dates = (t.demos || []).map((d) => d.uploaded_at).filter(Boolean);
    const latest = dates.length ? dates.sort().slice(-1)[0] : t.created_at;
    if (!latest) return "—";
    const d = new Date(latest);
    return isNaN(d) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const filtered = useMemo(() => talents.filter((t) =>
    (showArchived ? true : !t.archived) &&
    (!q || t.name.toLowerCase().includes(q.toLowerCase()) || (t.language||"").toLowerCase().includes(q.toLowerCase()))
  ), [talents, q, showArchived]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold">Voices <span className="text-zinc-400">({filtered.length})</span></h1>
        <button onClick={createNew} data-testid="add-talent-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover">
          <Plus size={16} /> Add talent
        </button>
      </div>
      <p className="mt-1 text-sm text-zinc-500">Tip: the ★ star marks a voice as <strong className="text-brand-violet">Featured on the homepage</strong> — click it to choose which voices appear there. The eye publishes/unpublishes.</p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or language…" className="pl-9 rounded-xl h-10 bg-white" data-testid="admin-talent-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-600"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived</label>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr><th className="p-3">Name</th><th className="p-3 hidden sm:table-cell">Language</th><th className="p-3 hidden md:table-cell">Type</th><th className="p-3 hidden md:table-cell">Demos</th><th className="p-3 hidden lg:table-cell">Date added</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} data-testid={`admin-talent-row-${t.id}`} className={`border-b border-zinc-100 last:border-0 ${t.archived ? "opacity-50" : ""}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-zinc-100">{t.photo_url && <img src={mediaUrl(t.photo_url)} alt="" className="h-full w-full object-cover" />}</div>
                    <Link to={`/admin/talents/${t.id}`} className="font-semibold text-zinc-800 hover:text-brand-violet">{t.name}</Link>
                  </div>
                </td>
                <td className="p-3 hidden sm:table-cell text-zinc-600">{t.language}</td>
                <td className="p-3 hidden md:table-cell"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${t.voice_type === "ai" ? "bg-violet-100 text-brand-violet" : "bg-zinc-100 text-zinc-600"}`}>{t.voice_type}</span></td>
                <td className="p-3 hidden md:table-cell text-zinc-600">{(t.demos || []).length}</td>
                <td className="p-3 hidden lg:table-cell text-zinc-600" data-testid={`date-added-${t.id}`}>{dateAdded(t)}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${t.published ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>{t.archived ? "Archived" : t.published ? "Published" : "Draft"}</span></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => patch(t, { featured: !t.featured })} title="Feature" data-testid={`feature-${t.id}`}
                      className={`flex h-8 w-8 items-center justify-center rounded-lg hover:bg-zinc-100 ${t.featured ? "text-amber-500" : "text-zinc-400"}`}><Star size={16} className={t.featured ? "fill-amber-500" : ""} /></button>
                    <button onClick={() => patch(t, { published: !t.published })} title={t.published ? "Unpublish" : "Publish"} data-testid={`publish-${t.id}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100">{t.published ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    <Link to={`/admin/talents/${t.id}`} title="Edit" className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100"><Pencil size={16} /></Link>
                    <button onClick={() => archive(t)} title="Archive" className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-500 hover:bg-red-50 hover:text-red-600"><Archive size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
